import type { Movie } from '@/types/movie';
import { movies } from '@/mocks/movies';
import { categories } from '@/mocks/categories';

export const allMovies = movies;

export function getAllMovies(): Movie[] {
  return movies;
}

export function getMovieById(id: string): Movie | undefined {
  return movies.find((m) => m.id === id);
}

export function getCategoryByKey(key: string) {
  return categories.find((c) => c.key === key);
}

export function getMoviesByType(type: string): Movie[] {
  return movies.filter((m) => m.type === type);
}

export function getLatestMovies(count = 20): Movie[] {
  return [...movies].sort((a, b) => b.year - a.year).slice(0, count);
}

export function getHotMovies(count = 10): Movie[] {
  return [...movies].sort((a, b) => b.views - a.views).slice(0, count);
}

export function getFeaturedMovies(count = 5): Movie[] {
  return [...movies].sort((a, b) => b.rating - a.rating).slice(0, count);
}

export function getRelatedMovies(movie: Movie, count = 10): Movie[] {
  const sameType = movies.filter((m) => m.id !== movie.id && m.type === movie.type);
  const tagMatch = movies.filter(
    (m) => m.id !== movie.id && m.type !== movie.type && m.tags.some((t) => movie.tags.includes(t)),
  );
  const rest = movies.filter((m) => m.id !== movie.id && m.type !== movie.type && !tagMatch.includes(m));
  return [...sameType, ...tagMatch, ...rest].slice(0, count);
}

export function matchYear(movie: Movie, yearKeys: string[]): boolean {
  if (yearKeys.length === 0) return true;
  const recent = movie.year >= 2021;
  if (yearKeys.includes('recent5') && recent) return true;
  if (yearKeys.includes('classic') && !recent) return true;
  return false;
}

export function filterMovies(
  list: Movie[],
  opts: { types?: string[]; regions?: string[]; years?: string[] },
): Movie[] {
  return list.filter((m) => {
    if (opts.types && opts.types.length > 0 && !opts.types.includes(m.type)) return false;
    if (opts.regions && opts.regions.length > 0 && !opts.regions.includes(m.region)) return false;
    if (opts.years && opts.years.length > 0 && !matchYear(m, opts.years)) return false;
    return true;
  });
}

export type SortKey = 'year_desc' | 'year_asc' | 'rating_desc';

export function sortMovies(list: Movie[], key: SortKey): Movie[] {
  const copy = [...list];
  if (key === 'year_desc') return copy.sort((a, b) => b.year - a.year);
  if (key === 'year_asc') return copy.sort((a, b) => a.year - b.year);
  return copy.sort((a, b) => b.rating - a.rating);
}

export function searchMovies(keyword: string): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return movies.filter(
    (m) =>
      m.title.toLowerCase().includes(q) ||
      m.region.toLowerCase().includes(q) ||
      m.director.toLowerCase().includes(q) ||
      m.actors.some((a) => a.toLowerCase().includes(q)) ||
      m.tags.some((t) => t.toLowerCase().includes(q)),
  );
}

export function formatViews(views: number): string {
  if (views >= 100000) return `${(views / 10000).toFixed(1)}万`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}